import React, { useContext } from 'react'
import styled from 'styled-components'

import { useI18n } from '@bestyled/primitives'

import {
  mediaqueries,
  GridLayoutContext,
  Section,
  Icons
} from '@slipway/theme-components'

import NavigationMenuAction from '../../components/Navigation.Menu.Action'
import Bio from '../../components/Bio'

import { ISpecialist } from '../../types'

const ArticlesHero: React.FC<{ specialists: ISpecialist[] }> = ({
  specialists
}) => {
  const { gridLayout = 'tiles', hasSetGridLayout, setGridLayout } = useContext(
    GridLayoutContext
  )

  const { locale, t } = useI18n()
  const tilesIsActive = hasSetGridLayout && gridLayout === 'tiles'
  const featuredSpecialist = specialists.find(specialist => specialist.featured)

  if (!featuredSpecialist) {
    throw new Error(`
      No featured Specialist found.
      Please ensure you have at least featured Specialist.
  `)
  }

  return (
    <Section relative id="Articles__Hero">
      <HeadingContainer style={{ maxWidth: `${t('site.hero.maxWidth')}px` }}>
        <HeroHeading
          dangerouslySetInnerHTML={{ __html: t('site.hero.title') }}
        />
      </HeadingContainer>
      <SubheadingContainer>
        <Bio specialist={featuredSpecialist} />
        <GridControlsContainer>
          <GridButton
            onClick={() => setGridLayout('tiles')}
            active={tilesIsActive}
            data-a11y="false"
            title={t('Articles.Show articles in Tile grid')}
            aria-label={t('Articles.Show articles in Tile grid')}
          >
            <Icons.Tiles />
          </GridButton>
          <GridButton
            onClick={() => setGridLayout('rows')}
            active={!tilesIsActive}
            data-a11y="false"
            title={t('Articles.Show articles in Row grid')}
            aria-label={t('Articles.Show articles in Row grid')}
          >
            <Icons.Rows />
          </GridButton>
        </GridControlsContainer>
      </SubheadingContainer>
      <ActionMenuContainer>
        <NavigationMenuAction />
      </ActionMenuContainer>
    </Section>
  )
}

export default ArticlesHero

const SubheadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 50px;

  ${mediaqueries.desktop`
    margin-bottom: 40px;
  `};

  ${mediaqueries.tablet`
    margin-bottom: 30px;
  `};

  ${mediaqueries.phablet`
    display: none;
  `};
`

const ActionMenuContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 100px;
  width: 100%;

  ${mediaqueries.desktop`
    margin-bottom: 80px;
  `};

  ${mediaqueries.tablet`
    margin-bottom: 60px;
  `};

  ${mediaqueries.phablet`
      display: none;
  `};
`

const GridControlsContainer = styled.div`
  display: flex;
  align-items: center;

  ${mediaqueries.tablet`
    display: none;
  `};
`

const HeadingContainer = styled.div`
  margin: 100px 0;

  ${mediaqueries.desktop`
    width: 80%;
  `}

  ${mediaqueries.tablet`
    width: 100%;
  `}
`

const HeroHeading = styled.h1`
  font-style: normal;
  font-weight: 600;
  font-size: 52px;
  line-height: 1.15;
  color: ${p => p.theme.colors.primary};

  a {
    color: ${p => p.theme.colors.accent};
  }

  ${mediaqueries.desktop`
    font-size: 38px
  `}

  ${mediaqueries.phablet`
    font-size: 32px;
  `}
`

const GridButton = styled.button<{ active: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 36px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.25s;

  &:not(:last-child) {
    margin-right: 30px;
  }

  &:hover {
    background: ${p => p.theme.colors.hover};
  }

  &[data-a11y='true']:focus::after {
    content: '';
    position: absolute;
    left: -10%;
    top: -10%;
    width: 120%;
    height: 120%;
    border: 2px solid ${p => p.theme.colors.accent};
    background: rgba(255, 255, 255, 0.01);
    border-radius: 50%;
  }

  svg {
    opacity: ${p => (p.active ? 1 : 0.25)};
    transition: opacity 0.2s;

    path {
      fill: ${p => p.theme.colors.primary};
    }
  }
`
